import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

import { trainingAdherence } from '@cf-shared/body-comp-overview';
import { Gender } from '@cf-shared/enums';
import { pickOneEntryPerSingletonSlot } from '@cf-shared/data-model/log-entry-user-data';
import { getAverageCalipersSum, getAverageWaist, Log_WeighIn } from '@cf-shared/data-model/log-weigh-in';
import { Log_WeeklyCheckup, readLoggedDayDates, readProgressSnapshot } from '@cf-shared/data-model/log-weekly-checkup';
import { getEntryLocalTimeInDesiredTimeZone, Log_Entry, LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';
import { averageValidNumbers } from '@cf-shared/shared-util';
import { buildPreviousWeekdayLogs, listDaysInRange } from 'pages/weekly-checkup/weekly-checkup-chart-data';
import { DocWatcher } from 'services/database-wrapper';
import { ConfigDbService } from 'services/config-db.service';
import { DebugService } from 'services/debug.service';
import { isRunningTrendChartsOnAsync } from 'services/running-trend-flag';
import { RUNNING_TREND_BODY_FAT_OPTIONS, runningTrendReadWindow } from '@cf-shared/running-trend';
import { SettingsService } from 'services/settings.service';
import { UserDb } from 'services/user-db.service';
import { formatShortDate, getMeasureUnitConversion, skinfoldSiteName, weekdayInitialsMondayFirst } from 'util/formatting';
import {
  BodyCompOverviewModel,
  OverviewCheckup,
  OverviewIntakeDay,
  OverviewPlanCheck,
  OverviewReading,
  OverviewSkinfoldSite,
  OverviewTrendKind,
  buildBodyCompOverviewModel,
} from './body-comp-overview-model';
import { ProgressStatusRule } from './progress-status';

/**
 * The request for the overview of ONE completed check-up: its stored verdict, the 14 days ending
 * on its date, and its own reported intake, week and ticked days. The client's completed view and
 * the coach's Body Composition tab (#5456, phase 2) both ask with this, so they show the same cards.
 */
export function completedCheckupOverviewRequest(checkup: Log_WeeklyCheckup, now: DateTime): BodyCompOverviewRequest {
  const lastDay = getEntryLocalTimeInDesiredTimeZone(checkup, now);
  return {
    firstDayIso: lastDay.minus({ days: 13 }).toISODate(),
    lastDayIso: lastDay.toISODate(),
    trendKind: OverviewTrendKind.Straight,
    describesNow: false,
    statusRule: ProgressStatusRule.Checkup,
    completedCheckup: checkup,
  };
}

/** how far before the range readings are loaded, so the running windows at its start are full */
const LOOKBACK_DAYS = 28;

/** how far back the latest completed check-up is looked for */
const CHECKUP_LOOKBACK_DAYS = 35;

/** the skinfold sites the app measures, in display order; thigh is measured for women only */
const SKINFOLD_SITES_MALE = ['suprailiac', 'umbilical'] as const;
const SKINFOLD_SITES_FEMALE = ['suprailiac', 'umbilical', 'thigh'] as const;
type SkinfoldSiteId = typeof SKINFOLD_SITES_FEMALE[number];

export interface BodyCompOverviewRequest {
  /** first and last calendar day of the range, as local ISO dates */
  firstDayIso: string;
  lastDayIso: string;
  trendKind: OverviewTrendKind;
  /** true when the range ends today: the cards may then describe the present moment */
  describesNow: boolean;
  statusRule: ProgressStatusRule;
  /** the plan check that judged the present moment, when the host has one */
  planCheck?: OverviewPlanCheck;
  /**
   * A completed check-up being viewed. The cards then describe that check-up: its stored
   * verdict, its reported intake, its week and its ticked days. The live plan check is not used.
   */
  completedCheckup?: Log_WeeklyCheckup;
  /**
   * A check-up that is being filled in. Cards 4 and 7 then show ITS week with these ticks, and
   * the reported intake comes from the previous completed check-up.
   */
  checkupInProgress?: { checkup: Log_WeeklyCheckup; tickedDayIsos?: string[] };
}

/**
 * Loads what the Body Composition overview needs and builds its model (#5456). Both hosts use
 * it: the Progress page and the weekly check-up.
 *
 * It reads only; it never runs the plan check itself (the host hands one in) and never touches a
 * pending calorie recommendation, so nothing it builds can show a coached client a number their
 * coach has not released. See design-docs/coaching-dashboard.md.
 */
@Injectable({ providedIn: 'root' })
export class BodyCompOverviewDataService {
  constructor(
    private userDb: UserDb,
    private settings: SettingsService,
    private debug: DebugService,
    private configDb: ConfigDbService,
  ) {}

  /**
   * Builds the overview for the requested range. Errors travel up: the host shows the
   * component's failed state, so "nothing logged" and "could not look" stay different.
   */
  async loadAsync(request: BodyCompOverviewRequest, watcher: DocWatcher): Promise<BodyCompOverviewModel> {
    const now = this.debug.getNowLocal();
    const firstDay = DateTime.fromISO(request.firstDayIso, { zone: now.zone }).startOf('day');
    const lastDay = DateTime.fromISO(request.lastDayIso, { zone: now.zone }).endOf('day');

    const immutableState = await this.userDb.loadImmutableStateAsync(watcher);
    const gender = immutableState?.gender || Gender.male;
    const siteIds: readonly SkinfoldSiteId[] = gender === Gender.male ? SKINFOLD_SITES_MALE : SKINFOLD_SITES_FEMALE;
    const skinfoldSites: OverviewSkinfoldSite[] = siteIds.map(id => ({ id, name: skinfoldSiteName(id) }));

    // with the running trend switch on (#6627), the readings come from the running trend's own read
    // window: 1.5 skinfold windows before the range and, for a past range, the days after it its
    // last days' fits reach into, never past today. The straight 2-week fit uses the range only
    const runningTrendOn = request.trendKind === OverviewTrendKind.Running && await isRunningTrendChartsOnAsync(this.configDb);
    const trendRead = runningTrendOn
      ? runningTrendReadWindow({ firstDayIso: request.firstDayIso, lastDayIso: request.lastDayIso, todayIso: now.toISODate() }, RUNNING_TREND_BODY_FAT_OPTIONS)
      : undefined;
    const readFrom = trendRead ? DateTime.fromISO(trendRead.fromIso, { zone: now.zone }).startOf('day') : firstDay.minus({ days: LOOKBACK_DAYS });
    const readUntil = trendRead ? DateTime.fromISO(trendRead.toIso, { zone: now.zone }).endOf('day') : lastDay;

    // the readings of the range, plus the weeks before it the running windows reach back into
    const entries = await this.userDb.getLogEntriesInRangeAsync(readFrom, readUntil, watcher, { inLogEntriesTimeZone: true });
    const readings = this.buildReadings(entries, gender, siteIds, now);

    const checkup = await this.loadCheckupCardsAsync(request, lastDay, now, watcher);
    // a stored snapshot can come from an older app version or be damaged, so it is read defensively
    const storedSnapshot = readProgressSnapshot(request.completedCheckup);

    const locale = this.settings.locale;
    return buildBodyCompOverviewModel({
      firstDayIso: request.firstDayIso,
      lastDayIso: request.lastDayIso,
      trendKind: request.trendKind,
      describesNow: request.describesNow,
      readings,
      runningTrendOn,
      todayIso: now.toISODate(),
      skinfoldSites,
      planCheck: request.planCheck,
      statusRule: request.statusRule,
      storedSnapshot,
      describesCompletedCheckup: !!request.completedCheckup,
      checkup,
      showReportedIntake: request.describesNow || !!request.completedCheckup,
      weightUnit: getMeasureUnitConversion('bodyweight_kg', locale, 'kg'),
      lengthUnit: getMeasureUnitConversion('bodydimensions_cm', locale, 'cm'),
      formatDate: dateIso => formatShortDate(DateTime.fromISO(dateIso), this.settings),
    });
  }

  /**
   * One reading per calendar day, in kg, mm and cm. A day that holds two weigh-in documents (a
   * reschedule, a second device) is read from the one the app's own one-per-day rule keeps, so an
   * empty placeholder never hides the weigh-in that holds the numbers.
   */
  private buildReadings(entries: Log_Entry[], gender: Gender, siteIds: readonly SkinfoldSiteId[], now: DateTime): OverviewReading[] {
    const weighInsByDay = new Map<string, Log_WeighIn[]>();
    for (const entry of entries) {
      if (entry?.type !== LogRecordType.WeighIn) continue;
      const dateIso = getEntryLocalTimeInDesiredTimeZone(entry, now).toISODate();
      weighInsByDay.set(dateIso, [...(weighInsByDay.get(dateIso) ?? []), entry as Log_WeighIn]);
    }
    const readings: OverviewReading[] = [];
    for (const [dateIso, dayWeighIns] of weighInsByDay) {
      const weighIn = pickOneEntryPerSingletonSlot(dayWeighIns, now).kept[0];
      if (!weighIn) continue;
      const reading: OverviewReading = { dateIso, sites: {} };
      if (typeof weighIn.weight_kg === 'number' && weighIn.weight_kg > 0) {
        reading.weight_kg = weighIn.weight_kg;
      }
      const skinfoldMean = getAverageCalipersSum(weighIn, gender);
      if (skinfoldMean && skinfoldMean > 0) {
        reading.skinfoldMean_mm = skinfoldMean;
      }
      const waist = getAverageWaist(weighIn);
      if (waist && waist > 0) {
        reading.waist_cm = waist;
      }
      for (const siteId of siteIds) {
        // a site can hold several readings of one measuring session; their mean is the site's value
        const value = averageValidNumbers(weighIn.caliperMeasures?.[`${siteId}_mm`], undefined);
        if (value && value > 0) {
          reading.sites[siteId] = value;
        }
      }
      readings.push(reading);
    }
    return readings;
  }

  /**
   * What cards 2, 4 and 7 show: whose reported intake, and which week.
   *
   * - A completed check-up being viewed: that check-up's own figures and week.
   * - A check-up being filled in: its own week with the ticks so far, and the intake reported at
   *   the previous completed check-up.
   * - Otherwise: the latest completed check-up on or before the end of the range.
   */
  private async loadCheckupCardsAsync(
    request: BodyCompOverviewRequest,
    lastDay: DateTime,
    now: DateTime,
    watcher: DocWatcher,
  ): Promise<OverviewCheckup | undefined> {
    const weekOwner = request.completedCheckup ?? request.checkupInProgress?.checkup;
    // the latest completed check-up before the one being filled in, or before the end of the range
    const searchEnd = request.checkupInProgress
      ? getEntryLocalTimeInDesiredTimeZone(request.checkupInProgress.checkup, now).startOf('day').minus({ milliseconds: 1 })
      : lastDay;
    const reported = request.completedCheckup ?? await this.findLatestCompletedCheckupAsync(searchEnd, now, watcher);
    const weekCheckup = weekOwner ?? reported;
    if (!weekCheckup) {
      return undefined;
    }

    const weekEnd = getEntryLocalTimeInDesiredTimeZone(weekCheckup, now).endOf('day');
    const weekStart = weekEnd.minus({ days: 6 }).startOf('day');
    const weekEntries = await this.userDb.getLogEntriesInRangeAsync(weekStart, weekEnd, watcher, { inLogEntriesTimeZone: true });
    const weekDayIsos = listDaysInRange(weekStart, weekEnd);
    const built = buildPreviousWeekdayLogs({ logEntries: weekEntries, days: weekDayIsos, weekdayInitials: weekdayInitialsMondayFirst(), now });

    // which days count: the in-progress wizard's ticks, else what the check-up stored. a check-up
    // that stored none (made before #5456) shows every day as counted
    const tickedDayIsos = request.checkupInProgress
      ? request.checkupInProgress.tickedDayIsos
      : readLoggedDayDates(weekCheckup);
    const ticked = tickedDayIsos ? new Set(tickedDayIsos) : undefined;
    const days: OverviewIntakeDay[] = built.days.map(day => ({
      dateIso: day.dateISO,
      label: day.weekdayLabel,
      consumed: day.consumedCalories,
      target: day.targetCalories,
      ticked: ticked ? ticked.has(day.dateISO) : true,
    }));

    const workouts = weekEntries.filter(e => e?.type === LogRecordType.Workout);
    return {
      dateIso: getEntryLocalTimeInDesiredTimeZone(reported ?? weekCheckup, now).toISODate(),
      calorieIntakeBaseline: reported?.calorieIntakeBaseline,
      selectedCalorieIntakeBaseline: reported?.selectedCalorieIntakeBaseline,
      hasTickedDays: !!ticked,
      days,
      training: trainingAdherence(workouts, weekDayIsos, now.toISODate(), now),
    };
  }

  /** The latest check-up that was completed on or before `until`, looking back a few weeks. */
  private async findLatestCompletedCheckupAsync(until: DateTime, now: DateTime, watcher: DocWatcher): Promise<Log_WeeklyCheckup | undefined> {
    const entries = await this.userDb.getLogEntriesInRangeAsync(
      until.minus({ days: CHECKUP_LOOKBACK_DAYS }).startOf('day'), until, watcher, { inLogEntriesTimeZone: true });
    const completed = entries
      .filter((e): e is Log_WeeklyCheckup => e?.type === LogRecordType.WeeklyCheckup && e.state === LogRecordState.Done)
      .filter(checkup => !!(checkup as Log_WeeklyCheckup).completionAction);
    if (completed.length === 0) {
      return undefined;
    }
    const time = (checkup: Log_WeeklyCheckup) => getEntryLocalTimeInDesiredTimeZone(checkup, now).toMillis();
    return completed.reduce((latest, checkup) => time(checkup) >= time(latest) ? checkup : latest);
  }
}
